import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPinPlus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CustomAreas() {
  const [states, setStates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [state, setState] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/locations/custom").then((r) => setAreas(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { api.get("/locations/states").then((r) => setStates(r.data.states)); load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!state || !name.trim()) return toast.error("Choose a state and enter the area name");
    setBusy(true);
    try { await api.post("/locations/custom", { state, name }); toast.success(`${name.trim()} added to ${state}`); setName(""); load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.name} (${a.state}) from the list?`)) return;
    try { await api.delete(`/locations/custom/${a.id}`); toast.success(`${a.name} removed`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="custom-areas">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="text-lg font-serif font-semibold">Cities &amp; Areas</h3>
        <p className="text-sm text-muted-foreground mt-0.5">Add a city or area inside an existing state. It appears right away in user access, corporate forms and filters.</p>
      </div>
      <form onSubmit={add} className="p-5 flex flex-wrap gap-3 items-end border-b border-border">
        <div className="w-56">
          <Select value={state} onValueChange={setState}>
            <SelectTrigger data-testid="custom-area-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="City / area name" className="w-64" maxLength={60} data-testid="custom-area-name-input" />
        <Button type="submit" disabled={busy} data-testid="custom-area-add-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MapPinPlus className="h-4 w-4 mr-1" />} Add Area
        </Button>
      </form>
      <div className="divide-y divide-border">
        {areas.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center" data-testid="custom-areas-empty">No custom areas added yet.</div>
        ) : areas.map((a) => {
          const inUse = a.corporates > 0 || a.users > 0;
          return (
            <div key={a.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`custom-area-row-${a.id}`}>
              <div>
                <div className="font-medium">{a.name} <span className="text-sm text-muted-foreground font-normal">· {a.state}</span></div>
                <div className="text-xs text-muted-foreground">Added by {a.created_by} · {a.corporates} corporate(s), {a.users} user(s)</div>
              </div>
              <Button size="sm" variant="ghost" className="text-destructive" disabled={inUse} title={inUse ? "In use — can't be removed" : "Remove"}
                onClick={() => remove(a)} data-testid={`custom-area-remove-${a.id}`}>
                <Trash2 className="h-4 w-4 mr-1" /> {inUse ? "In use" : "Remove"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
